import ExcelJS from "exceljs";
import { getImpLookupValueLists } from "./impProductValidation";

/** Canonical IMP product draft used by Excel import / Add IMP form. */
export function emptyImpProductDraft() {
  return {
    productName: "",
    productCode: "",
    dosageForm: "",
    strengthValue: "",
    strengthUnit: "",
    route: "",
    manufacturer: "",
    storageTempMin: "",
    storageTempMax: "",
    storageTempUnit: "C",
    storageInstructions: "",
    inventoryTrackingMethod: "IndividualKit",
  };
}

/** Format min/max + unit for table display, e.g. 2 – 8 °C */
export function formatStorageTempRange(minValue, maxValue, unit, fallback = "") {
  const minOk =
    minValue !== "" && minValue != null && Number.isFinite(Number(minValue));
  const maxOk =
    maxValue !== "" && maxValue != null && Number.isFinite(Number(maxValue));
  if (minOk && maxOk) {
    const min = Number(minValue);
    const max = Number(maxValue);
    const fmt = (n) =>
      Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
    const u = String(unit || "C").toUpperCase() === "F" ? "°F" : "°C";
    return `${fmt(min)} – ${fmt(max)} ${u}`;
  }
  return String(fallback || "").trim();
}

export function formatStrengthDisplay(value, unit, fallback = "") {
  const vOk = value !== "" && value != null && Number.isFinite(Number(value));
  const u = String(unit || "").trim();
  if (vOk && u) return `${Number(value)} ${u}`;
  return String(fallback || "").trim();
}

const HEADER_ALIASES = {
  productname: "productName",
  impname: "productName",
  name: "productName",
  productcode: "productCode",
  impcode: "productCode",
  code: "productCode",
  dosageform: "dosageForm",
  form: "dosageForm",
  strengthvalue: "strengthValue",
  strength: "strengthValue",
  strengthunit: "strengthUnit",
  unit: "strengthUnit",
  route: "route",
  routeofadministration: "route",
  manufacturer: "manufacturer",
  mfr: "manufacturer",
  storagetempmin: "storageTempMin",
  mintemp: "storageTempMin",
  mintemperature: "storageTempMin",
  storagetempmax: "storageTempMax",
  maxtemp: "storageTempMax",
  maxtemperature: "storageTempMax",
  storagetempunit: "storageTempUnit",
  temperatureunit: "storageTempUnit",
  tempunit: "storageTempUnit",
  storageinstructions: "storageInstructions",
  inventorytrackingmethod: "inventoryTrackingMethod",
  trackingmethod: "inventoryTrackingMethod",
  tracking: "inventoryTrackingMethod",
  inventorytracking: "inventoryTrackingMethod",
};

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function cellText(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.text != null) return String(value.text).trim();
  if (typeof value === "object" && value.result != null) {
    return String(value.result).trim();
  }
  return String(value).trim();
}

function cleanDraft(draft) {
  const next = emptyImpProductDraft();
  Object.keys(next).forEach((key) => {
    next[key] = String(draft?.[key] ?? next[key] ?? "").trim();
  });
  // Strip accidental HTML from name/code (paste / Excel rich text).
  next.productName = next.productName
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 150);
  next.productCode = next.productCode
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 50);
  if (!next.storageTempUnit) next.storageTempUnit = "C";
  // Preserve line breaks in instructions for HTML save later
  if (draft?.storageInstructions != null) {
    next.storageInstructions = String(draft.storageInstructions)
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .trim();
  }
  next.inventoryTrackingMethod = "IndividualKit";
  return next;
}

function isMeaningfulDraft(draft) {
  return Boolean(draft.productCode || draft.productName);
}

function parseTempCell(value) {
  const text = String(value || "")
    .trim()
    .replace(/°?\s*[cf]$/i, "")
    .trim();
  if (!text) return "";
  const n = Number(text);
  return Number.isFinite(n) ? String(n) : "";
}

function parseTempUnitCell(value) {
  const text = String(value || "").trim().toUpperCase();
  if (text === "F" || text === "°F" || text.includes("F")) return "F";
  if (text === "C" || text === "°C" || text.includes("C")) return "C";
  return "";
}

function parseTrackingMethodCell(value) {
  const text = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (!text) return "";
  if (
    text === "individualkit" ||
    text === "kit" ||
    text === "kits" ||
    text === "individual"
  ) {
    return "IndividualKit";
  }
  if (
    text === "batchquantity" ||
    text === "batch" ||
    text === "quantity" ||
    text === "batchqty"
  ) {
    return "BatchQuantity";
  }
  return "";
}

/** Template column order: Code first (matches Add IMP form). */
export const IMP_PRODUCT_EXCEL_HEADERS = [
  "IMP Code",
  "IMP Name",
  "Dosage Form",
  "Route",
  "Tracking Method",
  "Strength Value",
  "Strength Unit",
  "Manufacturer",
  "Temp Unit",
  "Min Temp",
  "Max Temp",
  "Storage Instructions",
];

export const TRACKING_METHOD_EXCEL_VALUES = [
  "Batch / Quantity",
  "Individual Kit",
];

function trackingMethodLabel(value) {
  return value === "IndividualKit" ? "Individual Kit" : "Batch / Quantity";
}

function applyHeaderDataValidation(sheet, listsSheet, lists) {
  const writeCol = (colIndex, values) => {
    (values || []).forEach((v, i) => {
      listsSheet.getCell(i + 1, colIndex).value = v;
    });
    return values?.length || 0;
  };
  const nDosage = writeCol(1, lists.dosageForms);
  const nRoute = writeCol(2, lists.routes);
  const nUnit = writeCol(3, lists.strengthUnits);
  const nTemp = writeCol(4, lists.tempUnits);
  const nTracking = writeCol(5, lists.trackingMethods);

  // IMPs columns: C Dosage, D Route, E Tracking, G Strength Unit, I Temp Unit
  const bindings = [
    { impsCol: "C", listCol: "A", count: nDosage },
    { impsCol: "D", listCol: "B", count: nRoute },
    { impsCol: "E", listCol: "E", count: nTracking },
    { impsCol: "G", listCol: "C", count: nUnit },
    { impsCol: "I", listCol: "D", count: nTemp },
  ];
  bindings.forEach(({ impsCol, listCol, count }) => {
    if (!count) return;
    sheet.dataValidations.add(`${impsCol}2:${impsCol}500`, {
      type: "list",
      allowBlank: true,
      formulae: [`Lists!$${listCol}$1:$${listCol}$${count}`],
      showErrorMessage: false,
    });
  });
}

export async function downloadImpProductExcelTemplate() {
  const wb = new ExcelJS.Workbook();
  const lists = getImpLookupValueLists();
  const sheet = wb.addWorksheet("IMPs");
  const listsSheet = wb.addWorksheet("Lists");
  listsSheet.state = "hidden";

  sheet.addRow(IMP_PRODUCT_EXCEL_HEADERS);
  sheet.addRow([
    "IMP-001",
    "Study Drug A",
    "Tablet",
    "Oral",
    trackingMethodLabel("IndividualKit"),
    "50",
    "mg",
    "ABC Pharma",
    "C",
    "2",
    "8",
    "Protect from light\nDo not freeze",
  ]);
  sheet.getRow(1).font = { bold: true };
  sheet.columns.forEach((col, i) => {
    col.width = i === 11 ? 32 : i === 4 ? 18 : 16;
  });
  applyHeaderDataValidation(sheet, listsSheet, lists);

  const notes = wb.addWorksheet("Instructions");
  notes.getColumn(1).width = 92;
  const lines = [
    "ePSF IMP Products — Excel import",
    "",
    "Scope",
    "- Products are created for the Study + Site currently selected in ePSF.",
    "- IMP Code must be unique per Study + Site (case-insensitive).",
    "",
    "Required columns",
    "- IMP Code, IMP Name, Dosage Form, Route",
    "- Temp Unit (C or F), Min Temp, Max Temp (min must be lower than max)",
    "",
    "Optional columns",
    "- Strength Value + Strength Unit (both required if either is filled)",
    "- Manufacturer (max 150 characters)",
    "- Storage Instructions (max 500 characters; use Alt+Enter for new lines)",
    "- Tracking Method column is ignored; all imported IMPs use Individual Kit",
    "",
    "Inventory tracking",
    "- All IMP products are saved as Individual Kit.",
    "- Kit numbers are required on receipt; each kit is tracked through custody.",
    "",
    "IMP Code rules",
    "- Max 50 characters",
    "- Letters, numbers, hyphen (-), underscore (_), slash (/) only",
    "- No spaces",
    "",
    "Duplicate handling",
    "- Same IMP Code twice in this file → rejected",
    "- IMP Code already active for the selected Study + Site → rejected",
    "",
    "Tips",
    "- Dosage Form / Route / Strength Unit / Temp Unit have dropdown lists on the IMPs sheet.",
    "- You may type a custom Dosage Form, Route, or Strength Unit if not listed.",
    "- Do not use the literal value Other — enter the real value instead.",
  ];
  lines.forEach((line, i) => {
    notes.getCell(i + 1, 1).value = line;
    if (i === 0) notes.getCell(i + 1, 1).font = { bold: true, size: 12 };
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "ePSF_IMP_Products_Template.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * @param {File} file
 * @param {{ existingRows?: any[] }} [options] reserved for callers; validation runs in UI
 */
export async function parseImpProductsFromExcelFile(file, options = {}) {
  if (!file) return { drafts: [], errors: [], message: "Choose an Excel file." };
  const name = String(file.name || "").toLowerCase();
  if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
    return { drafts: [], errors: [], message: "Upload an .xlsx Excel file." };
  }
  const wb = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await wb.xlsx.load(buffer);
  const sheet =
    wb.getWorksheet("IMPs") ||
    wb.worksheets.find((s) => /imp/i.test(s.name)) ||
    wb.worksheets[0];
  if (!sheet) return { drafts: [], errors: [], message: "The workbook has no sheets." };

  const rows = [];
  sheet.eachRow((row) => {
    const values = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      values[colNumber - 1] = cellText(cell.value);
    });
    rows.push({ values, excelRow: row.number });
  });
  if (rows.length < 2) {
    return {
      drafts: [],
      errors: [],
      message: "Add a header row and at least one product row.",
    };
  }

  const headerValues = rows[0].values.map(normalizeHeader);
  const index = {};
  headerValues.forEach((header, i) => {
    const field = HEADER_ALIASES[header];
    if (field && index[field] == null) index[field] = i;
  });

  const hasMappedHeaders =
    index.productCode != null || index.productName != null;

  const drafts = [];
  rows.slice(1).forEach(({ values, excelRow }) => {
    let draft;
    if (hasMappedHeaders) {
      draft = cleanDraft({
        productName: values[index.productName] || "",
        productCode: values[index.productCode] || "",
        dosageForm: values[index.dosageForm] || "",
        strengthValue: values[index.strengthValue] || "",
        strengthUnit: values[index.strengthUnit] || "",
        route: values[index.route] || "",
        manufacturer: values[index.manufacturer] || "",
        storageTempMin: parseTempCell(values[index.storageTempMin]),
        storageTempMax: parseTempCell(values[index.storageTempMax]),
        storageTempUnit: parseTempUnitCell(values[index.storageTempUnit]) || "C",
        storageInstructions: values[index.storageInstructions] || "",
        inventoryTrackingMethod: "IndividualKit",
      });
    } else {
      // New positional: Tracking at col 5 (index 4). Old files had Strength there.
      const looksLikeOldTemplate =
        Boolean(parseTempUnitCell(values[7])) &&
        !parseTrackingMethodCell(values[4]) &&
        (values[4] === "" || Number.isFinite(Number(values[4])));
      if (looksLikeOldTemplate) {
        draft = cleanDraft({
          productCode: values[0] || "",
          productName: values[1] || "",
          dosageForm: values[2] || "",
          route: values[3] || "",
          strengthValue: values[4] || "",
          strengthUnit: values[5] || "",
          manufacturer: values[6] || "",
          storageTempUnit: parseTempUnitCell(values[7]) || "C",
          storageTempMin: parseTempCell(values[8]),
          storageTempMax: parseTempCell(values[9]),
          storageInstructions: values[10] || "",
          inventoryTrackingMethod: "IndividualKit",
        });
      } else {
        draft = cleanDraft({
          productCode: values[0] || "",
          productName: values[1] || "",
          dosageForm: values[2] || "",
          route: values[3] || "",
          inventoryTrackingMethod: "IndividualKit",
          strengthValue: values[5] || "",
          strengthUnit: values[6] || "",
          manufacturer: values[7] || "",
          storageTempUnit: parseTempUnitCell(values[8]) || "C",
          storageTempMin: parseTempCell(values[9]),
          storageTempMax: parseTempCell(values[10]),
          storageInstructions: values[11] || "",
        });
      }
    }
    if (isMeaningfulDraft(draft)) {
      drafts.push({
        ...draft,
        id: `imp-import-${excelRow}-${drafts.length}`,
        __excelRow: excelRow,
      });
    }
  });

  if (!drafts.length) {
    return {
      drafts: [],
      errors: [],
      parseErrors: [],
      message:
        "No IMP rows found. Use Download template headers for Excel import.",
    };
  }

  return {
    drafts,
    errors: [],
    parseErrors: [],
    message: `Loaded ${drafts.length} IMP product${
      drafts.length === 1 ? "" : "s"
    } from Excel. Edit inline, then save.`,
  };
}
